<template>
  <PVToast
    v-bind="$attrs"
    class="t-toast"
    :position="toastPosition"
    :group="group"
    @life-end="$emit('life-end', $event.message)"
  >
    <!--
      #container replaces PrimeVue's entire message body. We need it (rather than
      #message) because the dismiss control lives in the coloured header band,
      not inline with the content where PrimeVue renders its own close button.
    -->
    <template #container="{ message, closeCallback }">
      <!-- nash-drop-shadow is a global class (utilClasses.scss), so it can't be @apply'd -->
      <div class="t-toast-card nash-drop-shadow" data-testid="toast-card">
        <div class="t-toast-header">
          <div class="t-toast-header-content">
            <TIcon
              v-if="iconFor(message, 'headerIcon')"
              :icon="iconFor(message, 'headerIcon')"
              size="xs"
              color="nash-neutral000"
              data-testid="toast-header-icon"
            />
            <slot name="header" :message="message">
              <span
                v-if="message.headerTitle"
                class="t-toast-header-title"
                data-testid="toast-header-title"
              >
                {{ message.headerTitle }}
              </span>
            </slot>
          </div>
          <!-- Dismissable unless a caller explicitly passes closable = false, mirrors PrimeVue's ToastMessage.vue default true check -->
          <button
            v-if="message.closable !== false"
            type="button"
            class="t-toast-close"
            :aria-label="closeLabel"
            data-testid="toast-close"
            @click="onClose(message, closeCallback)"
          >
            <TIcon :icon="closeIcon" size="xs" color="nash-neutral000" />
          </button>
        </div>

        <div class="t-toast-body">
          <slot name="text" :message="message">
            <div class="t-toast-text">
              <h4
                v-if="message.summary"
                class="t-toast-summary"
                data-testid="toast-summary"
              >
                {{ message.summary }}
              </h4>
              <p
                v-if="message.detail"
                class="t-toast-detail"
                data-testid="toast-detail"
              >
                {{ message.detail }}
              </p>
            </div>
          </slot>

          <slot name="action" :message="message">
            <div v-if="message.actionLabel" class="flex">
              <RouterLink
                v-if="message.actionTo"
                :to="message.actionTo"
                target="_blank"
                class="no-underline"
              >
                <TMPrimaryButton v-bind="actionButtonProps(message)" />
              </RouterLink>
              <TMPrimaryButton
                v-else
                v-bind="actionButtonProps(message)"
                @click="onAction(message)"
              />
            </div>
          </slot>
        </div>
      </div>
    </template>
  </PVToast>
</template>

<script lang="ts">
import Toast from 'primevue/toast'
import { defineComponent, SlotsType } from 'vue'
import { RouterLink } from 'vue-router'
import TMPrimaryButton from '../button/TMPrimaryButton.vue'
import TIcon from '../icon/TIcon.vue'
import { setup } from './controller'
import { T_TOAST_PROPS, type TToastMessage } from './types'

export default defineComponent({
  name: 'TToast',
  components: {
    PVToast: Toast,
    RouterLink,
    TIcon,
    TMPrimaryButton,
  },
  inheritAttrs: false,
  props: T_TOAST_PROPS,
  emits: ['action', 'close', 'life-end'],
  slots: Object as SlotsType<{
    header?: { message: TToastMessage }
    text?: { message: TToastMessage }
    action?: { message: TToastMessage }
  }>,
  setup,
})
</script>

<style lang="scss">
.t-toast {
  @apply print:hidden;

  // An app with chrome at the top of the viewport (thymebox: banners + nav bar)
  // publishes its height as `--t-toast-top-offset` so the stack clears it --
  // see useToastTopOffset.ts
  &.p-toast-top-left,
  &.p-toast-top-center,
  &.p-toast-top-right {
    margin-top: var(--t-toast-top-offset, 0px);
  }
}

// Visible card is `.t-toast-card`
.t-toast .p-toast-message {
  @apply border-0 bg-transparent shadow-none;
}
.t-toast .t-toast-card {
  @apply flex w-100 flex-col overflow-hidden rounded-xl border border-nash-neutral400 bg-nash-teal100;
  max-width: calc(100vw - 2.5rem);

  .t-toast-header {
    @apply flex h-9 shrink-0 items-center justify-between gap-2 bg-nash-teal700 px-4;
  }

  .t-toast-header-content {
    @apply flex min-w-0 items-center gap-1.5;
  }

  .t-toast-header-title {
    @apply truncate font-bold text-nash-neutral000;
    font-size: 14px;
  }

  .t-toast-close {
    @apply -m-1 flex shrink-0 items-center justify-center rounded-lg p-1 text-nash-neutral000;

    &:focus-visible {
      @apply outline outline-2 outline-offset-1 outline-nash-neutral000;
    }
  }

  .t-toast-body {
    @apply flex flex-col gap-3 px-4 pb-3.5 pt-3;
  }

  .t-toast-text {
    @apply flex flex-col gap-0.5;
  }

  .t-toast-summary {
    // 18px step (h4 in typography.scss); 140% per design, not the inherited 1.5
    @apply font-bold leading-[140%] text-nash-neutral900;
    // font-size: 1.286rem;
    font-family: var(--font-family);
    font-size: 18px;
  }

  .t-toast-detail {
    @apply font-normal text-nash-neutral800;
    font-size: 14px;
  }

  .p-button.p-button-sm {
    font-size: 14px;
  }

  .p-button .p-icon {
    font-size: 20px;
    min-width: 20px;
    min-height: 20px;
  }
}
</style>
